export type CoinSlug = string
export type CoinSymbol = string
export type CoinName = string
export type CoinId = number

export interface RSSFeed {
  url: string
  filters: string
  source?: string
}

export interface CoinResources {
  rss_feeds: RSSFeed[]
  website_urls: string[]
  whitepaper_urls: string[]
  whitepaper_urls_v2: {
    url: string
    is_mica: boolean
  }[]
  explorer_urls: string[]
  sourcecode_urls: string[]
  message_board_urls: string[]
  announcement_urls: string[]
  reddit_urls: string[]
  twitter_urls: string[]
  telegram_urls: string[]
  discord_urls: string[]
  sustainability_disclosure_urls: string[]
}

export interface CoinPlatform {
  name: CoinName
  slug: CoinSlug
  token_address: string
}

export interface CoinMeta extends CoinResources {
  id: CoinId
  name: CoinName
  symbol: CoinSymbol
  slug: CoinSlug
  source_slug: CoinSlug
  active: boolean
  trading_view_status: number
  trading_view_symbol: string
  icon?: string | null
  price_engine_enabled: boolean
  address?: string
  description: string
  localeDescription?: string
  meta_title: string
  platform?: CoinPlatform[]
  tags?: string[] | null
  is_defi_swap: boolean
  chat_urls: string[]
}

export interface CoinMarket {
  slug: CoinSlug
  app_tradable: boolean
  exchange_tradable: boolean
  defi_tradable: boolean
  prices: number[]
  circulating_supply: number
  total_supply: number
  max_supply: number | null
  btc_price: number
  btc_price_change_24h: number | null
  btc_marketcap: number
  btc_volume_24h: number
  usd_price: number
  usd_price_change_24h: number | null
  usd_marketcap: number
  usd_volume_24h: number
  usd_price_change_24h_abs: number | null
  token_dominance_rate: number | null
  rank: number
  update_time: number
  price_update_time: number
  isPriceAlive: boolean
  app_symbol: string
  token_id: number
  restricted_areas?: string[]
}

export type CoinFromAll = Pick<
  CoinMeta,
  'id' | 'name' | 'symbol' | 'slug' | 'source_slug' | 'icon' | 'price_engine_enabled'
> & {
  source_id: CoinId
}

// Global coins extends CoinFromAll with additional fields like rank
export type GlobalCoin = CoinFromAll & Pick<CoinMarket, 'rank'>

/**
 * CoinOverall extends CoinMeta and CoinMarket to provide a comprehensive
 * interface that combines both metadata (from CoinMeta) and market data (from CoinMarket).
 */
export interface CoinOverall extends CoinMeta, CoinMarket {}

export interface CryptoCurrency extends CoinOverall {
  community: any | null
  btc_price_update_time: number
  usd_price_update_time: number
  restricted_areas: any[]
  source_code: any | null
  page_title: string | null
  quote_source: string
}

export interface TopMoverItem {
  name: CoinName
  prices: number[]
  slug: CoinSlug
  symbol: CoinSymbol
  token_id: CoinId
  usd_price: number
  usd_price_change_24h: number
}

export interface TrendingCoin {
  slug: CoinSlug
  symbol: CoinSymbol
  name: CoinName
  token_id: CoinId
  usd_price_change_24h: number
}

export enum VisitType {
  Token = 1,
}

export interface PopularCoin {
  appTradeable: boolean
  exchangeTradable: boolean
  key: CoinId
  name: CoinName
  slug: CoinSlug
  symbol: CoinSymbol
  usdPriceChange24h: number
}

export enum PriceIndex {
  timestamp = 0,
  price = 1,
  marketCap = 2,
}

export interface CoinPrices {
  price_change: number
  prices: [number, number, number][] // [timestamp, price, market cap]
  usd_price_change: number
}

export interface Statistics {
  high: number
  low: number
  period: string
}

export interface CoinStatistics {
  statistics: Statistics[]
}

export type LiveCoinMarket = Partial<
  Pick<
    CoinOverall,
    'usd_price' | 'usd_marketcap' | 'usd_price_change_24h' | 'usd_volume_24h' | 'symbol' | 'slug'
  >
>
